import type { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Scene } from "@babylonjs/core/scene";
import type { MonsterSpriteGenerator } from "../assets/MonsterSpriteGenerator";
import type { SeededRandom } from "../core/SeededRandom";
import { PLAYER_CONFIG } from "../game/GameConfig";
import { applyDamage } from "../systems/DamageSystem";
import {
  resolveCircleMotionAgainstBody,
  type CircleCollisionBody,
  type HorizontalMotion,
} from "../systems/CollisionSystem";
import type { AudioSystem } from "../systems/AudioSystem";
import type { EnemySpawn } from "../generation/types";
import type { GridMap } from "../generation/GridMap";
import type { MaterialFactory } from "../rendering/MaterialFactory";
import { SpriteAnimator } from "../rendering/SpriteAnimator";
import { gridToWorld } from "../rendering/worldCoordinates";
import { ENEMY_CONFIGS, type EnemyConfig } from "./EnemyConfig";
import { EnemyNavigation, selectEnemyNavigationTarget } from "./EnemyNavigation";
import { perceivePlayer } from "./EnemyPerception";
import { EnemyState, EnemyStateMachine } from "./EnemyStateMachine";

export interface EnemyCallbacks {
  readonly damagePlayer: (amount: number) => void;
  readonly launchProjectile: (origin: Vector3, target: Vector3, damage: number) => void;
  readonly onKilled: (enemy: Enemy) => void;
  readonly alertAllies: (source: Enemy, position: Vector3) => void;
}

export class Enemy {
  public health: number;
  public readonly maximumHealth: number;
  public dead = false;
  public readonly mesh: Mesh;
  private readonly config: EnemyConfig;
  private readonly machine: EnemyStateMachine;
  private readonly navigation: EnemyNavigation;
  private readonly animator: SpriteAnimator;
  private readonly facing = new Vector3(0, 0, 1);
  private readonly lastKnownPlayerPosition = Vector3.Zero();
  private readonly resolvedMovement: HorizontalMotion = { x: 0, z: 0 };
  private readonly body: CircleCollisionBody;
  private heardRemaining = 0;
  private attackCooldown = 0;
  private previousState = EnemyState.Dormant;
  private alerted = false;

  public constructor(
    public readonly spawn: EnemySpawn,
    private readonly scene: Scene,
    grid: GridMap,
    materials: MaterialFactory,
    sprites: MonsterSpriteGenerator,
    private readonly random: SeededRandom,
    private readonly audio: AudioSystem,
    private readonly callbacks: EnemyCallbacks,
  ) {
    this.config = ENEMY_CONFIGS[spawn.kind];
    this.health = this.config.maximumHealth;
    this.maximumHealth = this.config.maximumHealth;
    this.machine = new EnemyStateMachine(3.5 + random.range(0, 1.8), 0.22 + random.range(0, 0.15));
    this.navigation = new EnemyNavigation(grid);
    const world = gridToWorld(spawn.cell, grid);
    this.mesh = MeshBuilder.CreatePlane(
      `enemy-${spawn.id}`,
      { width: this.config.width, height: this.config.height, sideOrientation: Mesh.DOUBLESIDE },
      scene,
    );
    this.mesh.position.set(world.x, this.config.height / 2, world.z);
    this.mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable = true;
    this.mesh.metadata = { enemyId: spawn.id, surface: "enemy" };
    this.body = { position: this.mesh.position, radius: this.config.collisionRadius };
    const material = materials.createAtlasMaterial(
      `enemy-material-${spawn.id}`,
      sprites.get(spawn.kind),
    );
    this.mesh.material = material;
    this.animator = new SpriteAnimator(material.diffuseTexture as Texture);
  }

  public update(deltaSeconds: number, playerPosition: Vector3, grid: GridMap): void {
    this.animator.update(deltaSeconds);
    if (this.dead) {
      if (this.animator.isFinished) this.animator.play("corpse");
      return;
    }

    this.heardRemaining = Math.max(0, this.heardRemaining - deltaSeconds);
    this.attackCooldown = Math.max(0, this.attackCooldown - deltaSeconds);
    const perception = perceivePlayer(
      grid,
      this.scene,
      this.mesh.position,
      this.facing,
      playerPosition,
      this.config.detectionRange,
      this.config.fieldOfViewRadians,
    );
    if (perception.seesPlayer) this.lastKnownPlayerPosition.copyFrom(playerPosition);
    const reachedLastKnown =
      Vector3.DistanceSquared(this.mesh.position, this.lastKnownPlayerPosition) < 1;
    const state = this.machine.update(deltaSeconds, {
      seesPlayer: perception.seesPlayer,
      heardPlayer: this.heardRemaining > 0,
      inAttackRange: perception.distance <= this.config.attackRange,
      hasLineOfSight: perception.hasLineOfSight,
      reachedLastKnownPosition: reachedLastKnown,
    });
    this.handleStateChange(state);

    if (state === EnemyState.Resurrection) return;

    if (!this.alerted && [EnemyState.Chase, EnemyState.Attack, EnemyState.Pain].includes(state)) {
      this.alerted = true;
      this.audio.play("enemy_alert", this.mesh.position);
      this.callbacks.alertAllies(this, this.lastKnownPlayerPosition);
    }

    if (state === EnemyState.Chase || state === EnemyState.Investigate) {
      const target = selectEnemyNavigationTarget(
        perception.seesPlayer,
        playerPosition,
        this.lastKnownPlayerPosition,
      );
      const movement = this.navigation.update(
        deltaSeconds,
        this.mesh.position,
        target,
        this.config.movementSpeed,
      );
      resolveCircleMotionAgainstBody(
        this.mesh.position,
        movement,
        this.config.collisionRadius,
        playerPosition,
        PLAYER_CONFIG.collisionRadius,
        this.resolvedMovement,
      );
      const movementLengthSquared =
        this.resolvedMovement.x * this.resolvedMovement.x +
        this.resolvedMovement.z * this.resolvedMovement.z;
      if (movementLengthSquared > 0) {
        const inverseLength = 1 / Math.sqrt(movementLengthSquared);
        this.facing.set(
          this.resolvedMovement.x * inverseLength,
          0,
          this.resolvedMovement.z * inverseLength,
        );
        this.mesh.position.x += this.resolvedMovement.x;
        this.mesh.position.z += this.resolvedMovement.z;
      }
    } else if (
      state === EnemyState.Attack &&
      this.attackCooldown <= 0 &&
      perception.hasLineOfSight
    ) {
      this.attack(playerPosition, perception.distance);
    }
  }

  public hear(position: Vector3): void {
    if (this.dead || Vector3.Distance(this.mesh.position, position) > this.config.hearingRange)
      return;
    this.lastKnownPlayerPosition.copyFrom(position);
    this.heardRemaining = 0.8 + this.random.range(0, 0.35);
  }

  public alertFromAlly(position: Vector3): void {
    if (this.dead) return;
    this.lastKnownPlayerPosition.copyFrom(position);
    this.heardRemaining = 1.1;
    this.machine.awakenByAlly();
  }

  public receiveDamage(amount: number, sourcePosition?: Vector3): void {
    const result = applyDamage(this, amount);
    if (result.applied <= 0) return;
    if (sourcePosition) this.lastKnownPlayerPosition.copyFrom(sourcePosition);
    if (result.killed) {
      this.machine.kill();
      this.dead = true;
      this.navigation.clear();
      this.mesh.isPickable = false;
      this.mesh.position.y = 0.55;
      this.animator.play("death", true);
      this.audio.play(this.deathSound(), this.mesh.position);
      this.callbacks.onKilled(this);
    } else {
      this.machine.receiveDamage();
      this.audio.play("enemy_pain", this.mesh.position);
      this.callbacks.alertAllies(this, this.mesh.position);
    }
  }

  public revive(healthFraction = 0.55): boolean {
    if (
      !this.dead ||
      this.spawn.kind === "forgeWarden" ||
      this.spawn.kind === "abyssTyrant"
    ) {
      return false;
    }
    this.dead = false;
    this.health = Math.max(1, Math.ceil(this.maximumHealth * healthFraction));
    this.machine.resurrect();
    this.navigation.clear();
    this.mesh.isPickable = true;
    this.mesh.position.y = this.config.height / 2;
    this.animator.play("resurrection", true);
    this.previousState = EnemyState.Resurrection;
    this.alerted = false;
    this.heardRemaining = 0;
    return true;
  }

  public turnCorpseToAsh(): void {
    if (!this.dead) return;
    this.mesh.scaling.y = 0.42;
    this.mesh.scaling.x = 0.78;
    this.mesh.position.y = 0.25;
  }

  public get position(): Vector3 {
    return this.mesh.position;
  }

  public get collisionBody(): CircleCollisionBody {
    return this.body;
  }

  public get state(): EnemyState {
    return this.machine.state;
  }

  private handleStateChange(state: EnemyState): void {
    if (state === this.previousState) return;
    this.previousState = state;
    switch (state) {
      case EnemyState.Dormant:
      case EnemyState.Idle:
        this.animator.play("idle");
        break;
      case EnemyState.Investigate:
      case EnemyState.Chase:
        this.animator.play("walk");
        break;
      case EnemyState.Attack:
        this.animator.play("attack", true);
        break;
      case EnemyState.Pain:
        this.animator.play("pain", true);
        break;
      case EnemyState.Resurrection:
        this.animator.play("resurrection", true);
        break;
      case EnemyState.Dead:
        this.animator.play("death", true);
        break;
    }
  }

  private attack(playerPosition: Vector3, distance: number): void {
    this.attackCooldown = this.config.attackCooldown + this.random.range(-0.12, 0.24);
    this.animator.play("attack", true);
    this.audio.play("enemy_attack", this.mesh.position);
    if (this.spawn.kind !== "guard" && this.spawn.kind !== "rustSentinel" && distance > 1.8) {
      const origin = this.mesh.position.add(new Vector3(0, 0.3, 0));
      this.callbacks.launchProjectile(origin, playerPosition.clone(), this.config.attackDamage);
    } else {
      this.callbacks.damagePlayer(this.config.attackDamage);
    }
  }

  private deathSound(): "enemy_death_guard" | "enemy_death_stalker" {
    return this.spawn.kind === "guard" || this.spawn.kind === "choirAcolyte"
      ? "enemy_death_guard"
      : "enemy_death_stalker";
  }
}
